/**
 * queue/components/modals.tsx
 * The four Host Console modals — walk-in, settle & free, no-show, entry detail.
 * Built on the shared catalog Modal primitive (themed monochrome). Each is a
 * controlled component driven by the console's local modal state.
 */

import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity } from 'react-native';
import { Modal } from '../../components/ui';
import { useTheme } from '../../context/themeContext';
import { useQueue } from '../store';
import { positionOf } from '../engine';
import { fmtPhone, fmtWait, waitMin, money, initials, clockHM } from '../format';
import { Mono, Stepper, StateChip } from './atoms';
import { CHANNEL_LABEL } from '../stateStyle';
import type { Channel } from '../types';

const CHANNELS: Channel[] = ['STAFF', 'VOICE', 'WEB', 'WHATSAPP', 'QR'];

// ── Walk-in ──────────────────────────────────────────────────────────────
export function WalkinModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { addWalkin } = useQueue();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [party, setParty] = useState(4);
  const [channel, setChannel] = useState<Channel>('STAFF');
  const [touched, setTouched] = useState(false);

  // Name + phone are MANDATORY now: the walk-in becomes a CRM contact (single
  // source of truth) on join. Phone must be exactly 10 digits — same rule as the
  // platform signup field (India: mobile.length === country digitCount).
  const digits = phone.replace(/\D/g, '');
  const nameOk = name.trim().length > 0;
  const phoneOk = digits.length === 10;
  const valid = nameOk && phoneOk;

  function reset() { setName(''); setPhone(''); setParty(4); setChannel('STAFF'); setTouched(false); }
  function submit(seatNow: boolean) {
    if (!valid) { setTouched(true); return; } // block until name + 10-digit phone
    addWalkin({ name: name.trim(), phone: digits, party, channel }, seatNow);
    reset(); onClose();
  }

  const footer = (
    <>
      <TouchableOpacity onPress={() => submit(false)} style={btn(colors, false)}><Text style={btnText(colors, false)}>Add to line</Text></TouchableOpacity>
      <TouchableOpacity onPress={() => submit(true)} style={btn(colors, true)}><Text style={btnText(colors, true)}>Add &amp; seat now ▸</Text></TouchableOpacity>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title="Add walk-in" footer={footer}>
      <Text style={{ fontSize: 12.5, color: colors.ink4, marginBottom: 18, marginTop: -4 }}>Name and mobile are required — the guest is saved to your contacts.</Text>
      <View style={{ gap: 18 }}>
        <View>
          <Text style={lbl(colors)}>Party size</Text>
          <Stepper value={party} onMinus={() => setParty(p => Math.max(1, p - 1))} onPlus={() => setParty(p => Math.min(20, p + 1))} />
        </View>
        <View>
          <Text style={lbl(colors)}>Name</Text>
          <TextInput value={name} onChangeText={setName} placeholder="e.g. Sharma" placeholderTextColor={colors.ink4}
            style={field(colors)} />
          {touched && !nameOk && <Text style={errText(colors)}>Name is required.</Text>}
        </View>
        <View>
          <Text style={lbl(colors)}>Mobile</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', height: 44, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 10, overflow: 'hidden' }}>
            <View style={{ paddingHorizontal: 12, borderRightWidth: 1, borderRightColor: colors.line, height: '100%', justifyContent: 'center' }}>
              <Mono style={{ color: colors.ink3 }}>+91</Mono>
            </View>
            <TextInput value={fmtPhone(phone)} onChangeText={(v) => setPhone((v || '').replace(/\D/g, '').slice(0, 10))}
              keyboardType="number-pad" placeholder="98765 43210" placeholderTextColor={colors.ink4}
              style={{ flex: 1, fontFamily: 'IBM Plex Mono', fontSize: 15, paddingHorizontal: 12, color: colors.ink } as any} />
          </View>
          {touched && !phoneOk && <Text style={errText(colors)}>Enter a 10-digit mobile number.</Text>}
        </View>
        <View>
          <Text style={lbl(colors)}>Source channel</Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {CHANNELS.map(ch => {
              const on = channel === ch;
              return (
                <TouchableOpacity key={ch} onPress={() => setChannel(ch)} style={{
                  flex: 1, paddingVertical: 9, borderRadius: 8, alignItems: 'center', borderWidth: 1,
                  backgroundColor: on ? colors.ink : colors.card, borderColor: on ? colors.ink : colors.line,
                }}>
                  <Text style={{ fontSize: 12.5, fontWeight: '500', color: on ? colors.white : colors.ink2 }}>{CHANNEL_LABEL[ch] || ch}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ── Settle & free ─────────────────────────────────────────────────────────
export function SettleModal({ open, onClose, resId }: { open: boolean; onClose: () => void; resId: string | null }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { state, settle } = useQueue();
  const r = resId ? state.resources.find(x => x.id === resId) : null;
  const occ = r?.occupant ? state.entries.find(e => e.id === r.occupant) : null;

  const footer = (
    <>
      <TouchableOpacity onPress={onClose} style={btn(colors, false)}><Text style={btnText(colors, false)}>Cancel</Text></TouchableOpacity>
      <TouchableOpacity onPress={() => { if (resId) settle(resId); onClose(); }} style={btn(colors, true)}><Text style={btnText(colors, true)}>Settle &amp; free</Text></TouchableOpacity>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title={`Settle & free ${r?.label ?? '—'}`} footer={footer} size="sm">
      <Text style={{ fontSize: 13.5, color: colors.ink3, lineHeight: 21 }}>
        Simulate <Text style={{ fontWeight: '700', color: colors.ink }}>Billing · order.settled</Text> for <Text style={{ fontWeight: '700', color: colors.ink }}>{occ?.displayName ?? '—'}</Text>. The table frees and the engine immediately allocates the next eligible party.
      </Text>
      <View style={{ marginTop: 14, backgroundColor: colors.paper, borderWidth: 1, borderColor: colors.paper3, borderRadius: 9, padding: 10 }}>
        <Mono style={{ fontSize: 12, color: colors.ink4 }}>resource.freed → allocate(FIFO, free) → CALLED</Mono>
      </View>
    </Modal>
  );
}

// ── No-show ─────────────────────────────────────────────────────────────────
export function NoShowModal({ open, onClose, entryId }: { open: boolean; onClose: () => void; entryId: string | null }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { state, noShow } = useQueue();
  const e = entryId ? state.entries.find(x => x.id === entryId) : null;

  const footer = (
    <>
      <TouchableOpacity onPress={onClose} style={btn(colors, false)}><Text style={btnText(colors, false)}>Cancel</Text></TouchableOpacity>
      <TouchableOpacity onPress={() => { if (entryId) noShow(entryId); onClose(); }} style={btn(colors, true)}><Text style={btnText(colors, true)}>Confirm no-show</Text></TouchableOpacity>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title="Mark no-show" footer={footer} size="sm">
      <Text style={{ fontSize: 13.5, color: colors.ink3, lineHeight: 21 }}>
        <Text style={{ fontWeight: '700', color: colors.ink }}>{e?.displayName ?? '—'}</Text> (party of {e?.party ?? ''}) didn't show after grace. This appends a <Text style={{ fontWeight: '700', color: colors.ink }}>NO_SHOW</Text> ledger row and frees any held table.
      </Text>
      {state.cfg2.no_show && (
        <View style={{ marginTop: 14, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.lineStrong, borderStyle: 'dashed', borderRadius: 9, padding: 10 }}>
          <Text style={{ fontSize: 12.5, color: colors.ink2 }}>A no-show charge of <Mono style={{ fontWeight: '700' }}>{money(state.cfg2.noShowAmt)}</Mono> will be raised via Billing.</Text>
        </View>
      )}
    </Modal>
  );
}

// ── Entry detail ────────────────────────────────────────────────────────────
export function EntryModal({
  open, onClose, entryId, onNoShow,
}: { open: boolean; onClose: () => void; entryId: string | null; onNoShow: (id: string) => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { state, autoSeat, seatCalled } = useQueue();
  const e = entryId ? state.entries.find(x => x.id === entryId) : null;
  if (!e) return <Modal open={open} onClose={onClose} title="Entry" />;

  const pos = positionOf(state.entries, e.id);
  const labels: Record<string, string> = {
    JOINED: 'Joined the line', VERIFIED: 'Number verified', WAITING: 'Waiting', CALLED: 'Called — table held',
    ALLOCATED: 'Allocated', SEATED: 'Seated', RELEASED: 'Released', REMOVED: 'Removed (self)', NO_SHOW: 'No-show', EXPIRED: 'Expired',
  };
  // allocatedResource is a resource ref (cal ULID in live mode) — show the table's label.
  const tableLabel = (e.allocatedResource && state.resources.find(r => r.id === e.allocatedResource)?.label) || e.allocatedResource || '';
  const posLabel = e.state === 'WAITING' ? `#${pos} · ${fmtWait(waitMin(pos ?? 0))}` : (e.allocatedResource ? `Table ${tableLabel}` : '—');

  const footer = (
    <>
      <TouchableOpacity onPress={onClose} style={btn(colors, false)}><Text style={btnText(colors, false)}>Close</Text></TouchableOpacity>
      {e.state === 'WAITING' && (
        <>
          <TouchableOpacity onPress={() => onNoShow(e.id)} style={btn(colors, false)}><Text style={btnText(colors, false)}>No-show</Text></TouchableOpacity>
          <TouchableOpacity onPress={() => { autoSeat(e.id); onClose(); }} style={btn(colors, true)}><Text style={btnText(colors, true)}>Auto-seat ▸</Text></TouchableOpacity>
        </>
      )}
      {e.state === 'CALLED' && (
        <>
          <TouchableOpacity onPress={() => onNoShow(e.id)} style={btn(colors, false)}><Text style={btnText(colors, false)}>No-show</Text></TouchableOpacity>
          <TouchableOpacity onPress={() => { seatCalled(e.id); onClose(); }} style={btn(colors, true)}><Text style={btnText(colors, true)}>Seat now ▸</Text></TouchableOpacity>
        </>
      )}
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title={e.displayName} footer={footer}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <View style={{ width: 46, height: 46, borderRadius: 23, backgroundColor: colors.paper3, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 15, fontWeight: '600', color: colors.ink2 }}>{initials(e.displayName)}</Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 12.5, color: colors.ink4 }}>+91 {fmtPhone(e.phone)} · #{e.token}</Text>
        </View>
        <StateChip state={e.state} compact />
      </View>

      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 18 }}>
        {[{ k: 'Party', v: String(e.party) }, { k: 'Channel', v: CHANNEL_LABEL[e.channel] || e.channel }, { k: 'Position', v: posLabel }].map(t => (
          <View key={t.k} style={{ flex: 1, borderWidth: 1, borderColor: colors.line, borderRadius: 10, padding: 11 }}>
            <Text style={{ fontSize: 10, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.k}</Text>
            <Text style={{ fontSize: 14, fontWeight: '600', marginTop: 4, color: colors.ink }}>{t.v}</Text>
          </View>
        ))}
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 18, padding: 9, backgroundColor: colors.paper, borderRadius: 8 }}>
        <Text style={{ fontSize: 11.5 }}>✓</Text>
        <Text style={{ fontSize: 11.5, color: colors.ink3 }}>{e.consent ? 'DPDP consent captured at self-join' : 'No consent on record'}</Text>
      </View>

      <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink4, marginBottom: 12 }}>Ledger · append-only</Text>
      <View>
        {[...e.events].reverse().map((ev, i) => (
          <View key={i} style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ alignItems: 'center' }}>
              <View style={{ width: 11, height: 11, borderRadius: 6, backgroundColor: i === 0 ? colors.ink : colors.card, borderWidth: i === 0 ? 0 : 2, borderColor: colors.lineStrong }} />
              <View style={{ width: 2, flex: 1, backgroundColor: colors.paper3, minHeight: 14 }} />
            </View>
            <View style={{ paddingBottom: 16, flex: 1 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{labels[ev.type] || ev.type}</Text>
                <View style={actorBadge(colors, ev.actor)}><Mono style={actorText(colors, ev.actor)}>{ev.actor}</Mono></View>
                <Mono style={{ marginLeft: 'auto', fontSize: 11, color: colors.ink4 }}>{clockHM(ev.at)}</Mono>
              </View>
              {ev.reason ? <Text style={{ fontSize: 12, color: colors.ink4, marginTop: 2 }}>{ev.reason}</Text> : null}
            </View>
          </View>
        ))}
      </View>
    </Modal>
  );
}

// ── shared style helpers ────────────────────────────────────────────────────
function btn(colors: any, dark: boolean) {
  return {
    height: 42, paddingHorizontal: 18, borderRadius: 10, alignItems: 'center' as const, justifyContent: 'center' as const,
    borderWidth: 1, backgroundColor: dark ? colors.ink : colors.card, borderColor: dark ? colors.ink : colors.lineStrong,
  };
}
function btnText(colors: any, dark: boolean) {
  return { fontSize: 14, fontWeight: dark ? '600' as const : '500' as const, color: dark ? colors.white : colors.ink2 };
}
function lbl(colors: any) { return { fontSize: 12.5, fontWeight: '600' as const, marginBottom: 7, color: colors.ink }; }
function errText(colors: any) { return { fontSize: 11.5, color: colors.ink3, marginTop: 5 }; }
function field(colors: any) {
  return { height: 44, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 10, paddingHorizontal: 14, fontSize: 14, color: colors.ink } as any;
}
export function actorBadge(colors: any, actor: string) {
  const isSys = actor === 'SYSTEM', isStaff = actor === 'STAFF';
  return {
    paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4,
    backgroundColor: isSys ? colors.ink : (isStaff ? colors.paper3 : colors.card),
    borderWidth: isSys ? 0 : (isStaff ? 0 : 1), borderColor: colors.lineStrong,
  };
}
export function actorText(colors: any, actor: string) {
  const isSys = actor === 'SYSTEM', isStaff = actor === 'STAFF';
  return { fontSize: 10, fontWeight: '600' as const, color: isSys ? colors.white : (isStaff ? colors.ink2 : colors.ink3) };
}
